import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useAppStore } from '@/store';
import { envConfig } from '@/configs';
import { useTranslation } from 'react-i18next';
import { getParsedPrice } from '@/utils/getParsedPrice';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useWalletPageContext } from './WalletPageContext';
import { useUniversityContext, useUserContext } from '@/context';
import { Flex, LoadingSpin, PaymentCard, SwitchGroup, A11y, Pagination, Scrollbar, LogTable, PermissionView } from '@/components';
import TransferCredit from './TransferCredit';
import IncrementCredit from './IncrementCredit';
import LegacyIncrementCredit from './LegacyIncrementCredit';
import { format } from 'date-fns-jalali/format';

import './style/index.css';
import { useQuery } from '@tanstack/react-query';
import { PaymentHttp } from '@/services';
import { ColumnBuilder } from '@/utils';
import { Typography } from 'antd';

const ALLOW_NEW_API = [
  'https://tst.samad.app',
  'https://ts1.samad.app',
  'https://ts2.samad.app',
  'https://ts3.samad.app',
];

const KEY_CREDIT_INC = 'CREDIT_INC';
const KEY_CREDIT_TRANSFER = 'CREDIT_TRANSFER';

type SlideCardKey = 'cash-credit' | 'subsidy-credit';

export function WalletPageContent() {

  const { profile } = useUserContext();
  const { university } = useUniversityContext();
  const [t] = useTranslation(['common', 'payment']);
  const [selectedCard, setSelectedCard] = useState<SlideCardKey>();
  const { credit: creditValue, subsidies, subsidyCredit, loadingCredit } = useAppStore();
  const { allowIncrease, gatewayInfo, transferInfo, loading, hasSubsidy } = useWalletPageContext();
  const [selectedTab, setSelectedTab] = useState(allowIncrease ? KEY_CREDIT_INC : KEY_CREDIT_TRANSFER);
  const [page, setPage] = useState<number>(0);
  const [pageSize] = useState<number>(10);


  const tabs = useMemo(() => {
    const arr = [];
    if (allowIncrease) {
      arr.push({ key: KEY_CREDIT_INC, name: t('common:labels.charge-credit') });
    }

    const types = profile?.group?.transferTypes;
    if (
      types == null ||
      selectedCard === 'cash-credit' && types?.includes('CASH') ||
      selectedCard === 'subsidy-credit' && types?.includes('SUBSIDY')
    ) {
      arr.push({ key: KEY_CREDIT_TRANSFER, name: t('payment:labels.transfer-credit') });
    }
    return arr;
  }, [t, allowIncrease, selectedCard, profile]);

  const slides = useMemo(() => {
    const result: { key: SlideCardKey; component: JSX.Element }[] = [];

    const cashCreditSlide = {
      key: 'cash-credit' as const,
      component: (
        <SwiperSlide key="slide-credit">
          <PaymentCard
            cardNumber={profile.username}
            valueText={getParsedPrice(creditValue)}
            hintText={t('payment:labels.cash-credit')}
            cardOwnerText={`${profile.firstName} ${profile.lastName}`} />
        </SwiperSlide>
      )
    };

    const subsidySlide = {
      key: 'subsidy-credit' as const,
      component: (
        <SwiperSlide key="subsidy-credit">
          <PaymentCard
            hintTextColor="#fff"
            className="card-blue"
            cardNumber={profile.username}
            valueText={getParsedPrice(subsidyCredit)}
            hintText={t('payment:labels.student-subsidy')}
            cardOwnerText={`${profile.firstName} ${profile.lastName}`} />
        </SwiperSlide>
      )
    }

    if (envConfig?.ui?.onlySubsidy) {
      result.push(subsidySlide);
    }
    else {
      result.push(cashCreditSlide);
      if (hasSubsidy) {
        result.push(subsidySlide);
      }
    }

    setSelectedCard(result[0].key);
    return result;
  }, [profile, creditValue, subsidyCredit, hasSubsidy])

  const tabsContent = useMemo(() => {
    if (loading) return {};
    const useNewAPI = window.location.origin === 'https://test.samad.app' || ALLOW_NEW_API.includes(university.universityURL);
    const obj = {};
    if (allowIncrease) {
      obj[KEY_CREDIT_INC] = useNewAPI ?
        <IncrementCredit gatewayInfo={gatewayInfo} key={KEY_CREDIT_INC} /> :
        <LegacyIncrementCredit gatewayInfo={gatewayInfo} key={KEY_CREDIT_INC} />;
    }
    obj[KEY_CREDIT_TRANSFER] = (
      <TransferCredit
        key={KEY_CREDIT_TRANSFER + selectedCard}
        transferInfo={transferInfo}
        isSubsidy={selectedCard === 'subsidy-credit'}
      />
    );
    return obj;
  }, [university, allowIncrease, gatewayInfo, subsidies, selectedCard, loading]);

  const { data, isFetching, error } = useQuery({
    queryKey: ['/payment-transactions/current', page],
    queryFn: async () => (await PaymentHttp.paymentReport(page, pageSize)).payload
  });

  let rows: any[];
  if (data) {
    rows = (data.content ?? []).map(item => ({
      ...item,
      successful: item.successful ? t('common:labels.success') : t('common:labels.fail'),
      amount: getParsedPrice(item.amount),
      date: format(item.startDate, 'yyyy/MM/dd'),
      time: item.startDate.split(' ')[1]
    }));
  }

  const columns = ColumnBuilder.create([
    // { key: 'type', title: t('common:labels.type') },
    { key: 'paymentCode', title: t('payment:labels.payment-code') },
    { key: 'bankName', title: t('payment:labels.bank-name'), align: 'center' },
    { key: 'successful', title: t('common:labels.status'), align: 'center' },
    { key: 'amount', title: `${t('payment:labels.amount')} ( ${t('common:labels.tomans')})`, align: 'center' },
    { key: 'date', title: t('common:labels.date'), align: 'center' },
    { key: 'time', title: t('common:labels.hour'), align: 'center' },

  ]).build();

  return (
    <Flex className="w-full mx-auto px-2 lg:px-4 overflow-hidden">
      <Flex direction="column" className="payment-container max-w-[400px] mx-auto ">
        {loadingCredit || loading ? (
          <Flex className="w-full h-full">
            <LoadingSpin className="m-auto text-sm text-nurture-darker" />
          </Flex>
        ) :
          <>
            <Swiper
              autoHeight
              slidesPerView="auto"
              direction="horizontal"
              spaceBetween={8}
              pagination={false}
              centeredSlides={true}
              scrollbar={{ draggable: true }}
              modules={[Pagination, Scrollbar, A11y]}
              onSlideChange={(swiper) => {
                setSelectedCard(slides[swiper.activeIndex].key);
              }}
              className={clsx(
                // 'app-swiper-enhanced-scale',
                { 'swiper-single': !hasSubsidy }
              )}
              style={{ width: '100%', minHeight: 175, marginTop: '0rem', marginBottom: '0.5rem' }}
            >
              {slides.map(slide => slide.component)}
            </Swiper>
            <SwitchGroup
              items={tabs}
              selected={selectedTab}
              onTabChange={key => setSelectedTab(key)}
            />
            {tabsContent[selectedTab]}
          </>
        }
      </Flex>
      <PermissionView required="ROLE_USER_VIEW_OWN_PAYMENTS">
        {!error &&
          <Flex direction="column" className="payment-container aspect-auto overflow-auto">
            <Typography.Title level={4} type="success">
              {t('payment:labels.transaction-history')}
            </Typography.Title>
            {isFetching || loading ? (
              <Flex className="w-full h-full">
                <LoadingSpin className="m-auto text-sm text-nurture-darker" />
              </Flex>
            ) :
              <>
                <LogTable
                  size="small"
                  rows={rows}
                  className="no-last-th-border"
                  columns={columns as any}
                  loading={loading}
                  pagination={{
                    total: data.totalElements ? data.totalElements : 0,
                    current: page + 1,
                    pageSize: 10,
                    onChange: page => setPage(page - 1),
                    showSizeChanger: false,
                  }}
                />
              </>
            }
          </Flex>}
      </PermissionView>
    </Flex>
  );
}

export default WalletPageContent;
